/**
 * Coding mode: an assistant answer in the normal chat whose text is a tagged JSON envelope.
 * Both the helper chat and the learner/interview page detect the envelope and render it as an
 * exact before/after diff instead of markdown. Nothing else about the conversation changes.
 */
export const CODING_LANGUAGES = ['python', 'java', 'javascript', 'typescript', 'bash', 'c', 'cpp', 'sql'] as const;
export type CodingLanguage = typeof CODING_LANGUAGES[number];

export const LANGUAGE_LABELS: Record<CodingLanguage, string> = {
  python: 'Python', java: 'Java', javascript: 'JavaScript', typescript: 'TypeScript', bash: 'Bash', c: 'C', cpp: 'C++',
  sql: 'SQL'
};

export interface CodeFile {
  id: string;
  path: string;
  language: CodingLanguage;
  content: string;
}

/** A plain-English explanation; `anchor` is an exact snippet of the changed code it belongs to ('' = whole file). */
export interface CodingNote {
  fileId: string;
  anchor: string;
  text: string;
}

export interface CodingAnswer {
  v: 1;
  language: CodingLanguage;
  problem: string;
  summary: string;
  /** Non-empty when the model could not proceed (blurry screenshot, ambiguous request). No files then. */
  clarification: string;
  /** The code before this answer: previous coding answer's files, or code transcribed from a screenshot. */
  baseline: CodeFile[];
  /** The code after this answer. */
  files: CodeFile[];
  notes: CodingNote[];
  /** True when `baseline` was read from a screenshot/paste in this very answer. */
  transcribed: boolean;
  /** True while the answer is still streaming in: show what has arrived, no diff yet. Never saved. */
  streaming?: boolean;
}

export const CODING_FENCE = '```otter-coding';

/** Text of a coding-mode reply that failed or was cancelled; such replies are never used as context. */
export const CODING_FAILURE_PREFIX = 'Sorry, Otter AI could not prepare the code changes.';
/** What users see when a coding answer fails. It never includes technical details. */
export const CODING_FAILURE_MESSAGE = CODING_FAILURE_PREFIX + ' Please try again.';
export const CODING_CANCELLED_TEXT = 'Cancelled.';

export function isFailedCodingReply(text: string | null | undefined): boolean {
  const value = (text || '').trim();
  return value.startsWith(CODING_FAILURE_PREFIX) || value === CODING_CANCELLED_TEXT;
}

export function isCodingAnswer(text: string | null | undefined): boolean {
  return typeof text === 'string' && text.trimStart().startsWith(CODING_FENCE);
}

export function encodeCodingAnswer(answer: CodingAnswer): string {
  return `${CODING_FENCE}\n${JSON.stringify(answer)}\n\`\`\``;
}

const parsed = new Map<string, CodingAnswer | null>();

/** Parses an envelope produced by {@link encodeCodingAnswer}; null for anything else. Results are memoised. */
export function parseCodingAnswer(text: string | null | undefined): CodingAnswer | null {
  if (!isCodingAnswer(text)) return null;
  const key = text as string;
  if (parsed.has(key)) return parsed.get(key)!;
  let result: CodingAnswer | null = null;
  try {
    const body = key.trim().slice(CODING_FENCE.length).replace(/```\s*$/, '');
    const raw: unknown = JSON.parse(body);
    if (isRecord(raw) && Array.isArray(raw['files']) && Array.isArray(raw['baseline'])) {
      validateCodeFiles(raw['files']);
      validateCodeFiles(raw['baseline']);
      const language = CODING_LANGUAGES.find(lang => lang === raw['language']) || raw['files'][0]?.language || 'python';
      const notes = Array.isArray(raw['notes']) ? raw['notes'].filter(isRecord).map(note => ({
        fileId: String(note['fileId'] ?? ''), anchor: String(note['anchor'] ?? ''), text: String(note['text'] ?? '')
      })) : [];
      result = {
        v: 1, language, problem: String(raw['problem'] ?? ''), summary: String(raw['summary'] ?? ''),
        clarification: String(raw['clarification'] ?? ''), baseline: raw['baseline'], files: raw['files'], notes,
        transcribed: raw['transcribed'] === true,
        ...(raw['streaming'] === true ? { streaming: true } : {})
      };
    }
  } catch (error) {
    console.warn('[CodingMode] Could not parse coding answer', error);
  }
  if (parsed.size > 200) parsed.clear();
  parsed.set(key, result);
  return result;
}

/** Keeps only the files of a partial reply that are complete enough to show (id, path and language known). */
function streamingFiles(value: unknown, fallback?: CodingLanguage): CodeFile[] {
  if (!Array.isArray(value)) return [];
  const files: CodeFile[] = [];
  // The same repairs as the finished reply, so a file is drawn with the id, name and language it will end with.
  for (const file of repairFiles(value, fallback, new Map()) as unknown[]) {
    if (!isRecord(file) || typeof file['content'] !== 'string') continue;
    const candidate = { id: file['id'], path: file['path'], language: file['language'], content: file['content'] };
    try {
      validateCodeFiles([...files, candidate]);
      files.push(candidate as CodeFile);
    } catch {
      // Not shown until its id, path and language have fully arrived.
    }
  }
  return files;
}

/**
 * Builds the answer to show while the model's reply is still streaming in, from whatever part of the JSON has
 * arrived: the summary as it is written and the code of each file as it is typed. Returns null until there is
 * something to show. The finished reply replaces it with the full answer (fresh code or the red/green diff).
 */
export function streamingCodingAnswer(partial: unknown, state: CodingState, allowBaseline = true): CodingAnswer | null {
  if (!isRecord(partial)) return null;
  const text = (key: string) => typeof partial[key] === 'string' ? partial[key] as string : '';
  // No code is shown until the fields that decide how it is drawn have arrived (new question or change, code read
  // from a screenshot or not). Models do not always write the fields in order, and code drawn one way while it
  // streams and redrawn another way when the answer completes is exactly the blink the helper must never see.
  const flag = partial['newProblem'];
  const newProblem = flag === true || flag === 'true' ? true : flag === false || flag === 'false' ? false : undefined;
  const decided = (state.files.length === 0 || newProblem !== undefined) && (!allowBaseline || 'baseline' in partial);
  const fallback = toCodingLanguage(partial['language']);
  const files = decided ? streamingFiles(partial['files'], fallback) : [];
  const baseline = allowBaseline ? streamingFiles(partial['baseline'], fallback) : [];
  if (!text('summary') && !text('clarification') && !files.length && !baseline.length) return null;
  const language = fallback || files[0]?.language || baseline[0]?.language || state.language || 'python';
  // Mirror what the finished answer will be (see parseCodingReply): code read from a screenshot, the current code
  // for a change, or nothing for a new question and for a "change" that kept none of the current files.
  let before: CodeFile[] = [];
  if (baseline.length) {
    alignFileIds(baseline, files, true);
    before = baseline;
  } else if (newProblem === false && state.files.length > 0) {
    alignFileIds(state.files, files, false);
    before = !files.length || files.some(file => state.files.some(old => old.id === file.id)) ? state.files : [];
  }
  return {
    v: 1, language, problem: text('problem') || state.problem, summary: text('summary'), clarification: text('clarification'),
    baseline: before, files, notes: [], transcribed: baseline.length > 0, streaming: true
  };
}

/** Markdown fallback used for clipboard copies and as model context for later ordinary questions. */
export function codingAnswerToMarkdown(answer: CodingAnswer): string {
  const parts: string[] = [];
  if (answer.summary) parts.push(answer.summary);
  if (answer.clarification) parts.push(`Needs clarification: ${answer.clarification}`);
  for (const file of answer.files) {
    parts.push(`**${file.path}**\n\`\`\`${file.language}\n${file.content.replace(/\n$/, '')}\n\`\`\``);
  }
  for (const note of answer.notes) parts.push(`- ${note.text}`);
  return parts.join('\n\n');
}

export interface CodingState {
  files: CodeFile[];
  language: CodingLanguage | null;
  problem: string;
  /** The helper's previous coding requests in this conversation, oldest first. */
  requests: string[];
}

const stripHtml = (text: string): string => text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * Derives the current code from the conversation: the last coding answer that produced files is the
 * baseline for the next request, so follow-ups ("rename start to wordStart") stay surgical.
 */
export function latestCodingState(messages: ReadonlyArray<{ sender: string; text: string }>): CodingState {
  const state: CodingState = { files: [], language: null, problem: '', requests: [] };
  let pendingUser = '';
  for (const message of messages) {
    if (message.sender === 'user') {
      pendingUser = stripHtml(message.text);
      continue;
    }
    const answer = parseCodingAnswer(message.text);
    if (!answer || answer.streaming) continue;
    // A fresh answer (no previous code shown) starts a new problem: earlier requests no longer apply.
    if (!answer.clarification && answer.baseline.length === 0) state.requests = [];
    if (pendingUser) state.requests.push(pendingUser);
    pendingUser = '';
    state.language = answer.language;
    if (answer.problem) state.problem = answer.problem;
    if (!answer.clarification) state.files = answer.files;
  }
  state.requests = state.requests.slice(-10);
  return state;
}

export function validateCodeFiles(value: unknown, label = 'file list'): asserts value is CodeFile[] {
  if (!Array.isArray(value)) {
    throw new Error(`The AI reply's ${label} is missing or not a list.`);
  }
  if (value.length > 20) {
    throw new Error('Use at most 20 files per coding answer.');
  }
  const ids = new Set<string>();
  const paths = new Set<string>();
  for (const file of value) {
    if (!isRecord(file) || typeof file['id'] !== 'string' || !/^[A-Za-z0-9_-]{1,100}$/.test(file['id'])
        || ids.has(file['id'])) {
      throw new Error('Each file needs a unique ID.');
    }
    const path = file['path'];
    if (typeof path !== 'string' || path.length > 200 || !/^[A-Za-z0-9_.-]+(\/[A-Za-z0-9_.-]+)*$/.test(path)
        || path.split('/').some(part => part === '.' || part === '..') || paths.has(path)) {
      throw new Error('Use unique relative filenames, for example src/Main.java (no spaces or "..").');
    }
    if (!CODING_LANGUAGES.some(language => language === file['language'])) {
      throw new Error('Choose a supported programming language for each file.');
    }
    const code = file['content'];
    if (typeof code !== 'string' || code.length > 40_000 || code.split('\n').length > 1000
        || code.split('\n').some(line => line.length > 1000)) {
      throw new Error('Each file is limited to 40,000 characters, 1000 lines and 1000 characters per line.');
    }
    ids.add(file['id']);
    paths.add(path);
  }
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export interface CodingReply {
  language: CodingLanguage;
  /** True when the request is a new, separate question: shown as a fresh answer, never as a diff. */
  newProblem: boolean;
  problem: string;
  clarification: string;
  summary: string;
  /** Current code read from the screenshot/paste, or null when the previous files are the baseline. */
  baseline: CodeFile[] | null;
  files: CodeFile[];
  deletedFileIds: string[];
  notes: CodingNote[];
}

/**
 * Parses and strictly validates the model's JSON reply. `current` are the files from the previous coding
 * answer; when the reply carries a transcribed `baseline`, that is what `files` must account for instead.
 */
/** Parses the reply text: plain JSON, fenced JSON, or JSON with stray prose around it. */
function parseReplyObject(text: string): Record<string, unknown> {
  const trimmed = text.trim().replace(/^```(?:json)?\s*\n([\s\S]*?)\n```\s*$/, '$1');
  const attempts = [trimmed];
  const first = trimmed.indexOf('{'), last = trimmed.lastIndexOf('}');
  if (first > 0 || (last >= 0 && last < trimmed.length - 1)) attempts.push(trimmed.slice(first, last + 1));
  for (const attempt of attempts) {
    try {
      const raw: unknown = JSON.parse(attempt);
      if (isRecord(raw)) return raw;
    } catch { /* try the next form */ }
  }
  throw new Error('The AI did not return a coding answer. Please retry.');
}

/** Models sometimes send a nested list as a JSON string (a known tool-input quirk); unwrap it. */
function unwrapJsonString(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed || trimmed === 'null') return null;
  if (!/^[[{]/.test(trimmed)) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

const LANGUAGE_ALIASES: Record<string, CodingLanguage> = {
  'c++': 'cpp', cplusplus: 'cpp', js: 'javascript', node: 'javascript', nodejs: 'javascript', ts: 'typescript',
  py: 'python', python3: 'python', sh: 'bash', shell: 'bash', zsh: 'bash', mysql: 'sql', postgresql: 'sql',
  postgres: 'sql', sqlite: 'sql', plsql: 'sql', 'pl/sql': 'sql', tsql: 'sql', 't-sql': 'sql'
};
const EXTENSION_LANGUAGES: Record<string, CodingLanguage> = {
  py: 'python', java: 'java', js: 'javascript', mjs: 'javascript', ts: 'typescript', sh: 'bash', c: 'c', h: 'c',
  cpp: 'cpp', cc: 'cpp', cxx: 'cpp', hpp: 'cpp', sql: 'sql'
};
const FILE_ID = /^[A-Za-z0-9_-]{1,100}$/;

/** A supported language from what the model wrote ("Java", "C++", "py") or, failing that, from the file extension. */
export function toCodingLanguage(value: unknown, path = ''): CodingLanguage | undefined {
  const name = typeof value === 'string' ? value.trim().toLowerCase() : '';
  const known = CODING_LANGUAGES.find(language => language === name) || LANGUAGE_ALIASES[name];
  if (known) return known;
  return EXTENSION_LANGUAGES[(/\.([a-z0-9]+)$/i.exec(path)?.[1] || '').toLowerCase()];
}

/**
 * Repairs the harmless slips in a file list so a good answer is never thrown away for them: an id that is a
 * filename ("Main.java"), a language written as "Java" or "C++", spaces or "./" in a path. Code is never touched.
 * Changed ids are recorded in `ids` so notes and deletions that use the model's id still find their file.
 */
function repairFiles(value: unknown, fallback: CodingLanguage | undefined, ids: Map<string, string>): unknown {
  if (!Array.isArray(value)) return value;
  const used = new Set<string>();
  return value.map((file, index) => {
    if (!isRecord(file)) return file;
    const path = typeof file['path'] === 'string'
      ? file['path'].trim().replace(/\\/g, '/').replace(/^(\.\/|\/)+/, '').replace(/\s+/g, '_') : file['path'];
    const given = typeof file['id'] === 'string' ? file['id'].trim() : '';
    let id = FILE_ID.test(given) ? given
      : (given || (typeof path === 'string' ? path.split('/').pop() : '') || '').replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 90)
        || `file${index + 1}`;
    for (let n = 2; used.has(id); n++) id = `${id.replace(/-\d+$/, '')}-${n}`;
    used.add(id);
    if (given && given !== id) ids.set(given, id);
    const language = toCodingLanguage(file['language'], typeof path === 'string' ? path : '') || fallback || file['language'];
    return { ...file, id, path, language };
  });
}

/**
 * Brings the common, harmless variations of a reply into the strict shape: list fields sent as JSON strings,
 * an empty baseline instead of null, missing optional lists and text fields, `newProblem` sent as "true" / "false",
 * over-long text, and the file slips repairFiles fixes (notes and deletions follow a file whose id was repaired).
 */
function normalizeReply(raw: Record<string, unknown>): Record<string, unknown> {
  const reply: Record<string, unknown> = { ...raw };
  for (const field of ['baseline', 'files', 'deletedFileIds', 'notes']) reply[field] = unwrapJsonString(reply[field]);
  if (Array.isArray(reply['baseline']) && reply['baseline'].length === 0) reply['baseline'] = null;
  if (reply['baseline'] === undefined || reply['baseline'] === '') reply['baseline'] = null;
  if (reply['deletedFileIds'] === undefined || reply['deletedFileIds'] === null) reply['deletedFileIds'] = [];
  if (reply['notes'] === undefined || reply['notes'] === null) reply['notes'] = [];
  for (const field of ['problem', 'clarification', 'summary']) {
    const value = reply[field];
    reply[field] = typeof value === 'string' ? value.slice(0, 4000) : value === undefined || value === null ? '' : value;
  }
  if (reply['newProblem'] === 'true' || reply['newProblem'] === 'false') reply['newProblem'] = reply['newProblem'] === 'true';
  const language = toCodingLanguage(reply['language']);
  if (language) reply['language'] = language;
  const ids = new Map<string, string>();
  reply['files'] = repairFiles(reply['files'], language, ids);
  reply['baseline'] = repairFiles(reply['baseline'], language, ids);
  if (Array.isArray(reply['deletedFileIds'])) {
    reply['deletedFileIds'] = reply['deletedFileIds'].map(id => typeof id === 'string' ? ids.get(id) ?? id : id);
  }
  if (Array.isArray(reply['notes'])) {
    reply['notes'] = reply['notes'].map(note => isRecord(note) && typeof note['fileId'] === 'string' && ids.has(note['fileId'])
      ? { ...note, fileId: ids.get(note['fileId']) } : note);
  }
  return reply;
}

/**
 * Gives a file back the id of the earlier file it replaces when only its id changed: the same filename, or, for code
 * read from a screenshot, the only read file and the only answer file. Mutates `files`; returns new id -> old id.
 * Used both while streaming and on the finished reply, so both draw the answer the same way.
 */
function alignFileIds(before: CodeFile[], files: CodeFile[], pairSingle: boolean): Map<string, string> {
  const renamed = new Map<string, string>();
  const unmatched = () => before.filter(old => !files.some(file => file.id === old.id));
  const unknown = () => files.filter(file => !before.some(old => old.id === file.id));
  const keep = (file: CodeFile, old: CodeFile) => {
    renamed.set(file.id, old.id);
    file.id = old.id;
  };
  for (const old of unmatched()) {
    const match = unknown().find(file => file.path === old.path);
    if (match) keep(match, old);
  }
  if (pairSingle && unmatched().length === 1 && unknown().length === 1) keep(unknown()[0], unmatched()[0]);
  return renamed;
}

/**
 * True when the helper's message itself contains code (pasted or typed), which the model may copy into
 * "baseline" as the learner's code. Without a screenshot or such code there is nothing to read: the current code
 * is already known, so a baseline the model returns anyway is ignored.
 */
export function containsPastedCode(text: string): boolean {
  if (/```/.test(text)) return true;
  const codeLike = /[;{}=]|\w\s*\(.*\)|^\s*(def|class|import|from|for|while|if|elif|else|return|print|public|private|static|int|void|select|insert|update|delete|where)\b/i;
  return text.split('\n').filter(line => line.trim() && codeLike.test(line)).length >= 2;
}

/**
 * Validates the model's reply against the current files. `allowBaseline` is false when the request had neither a
 * screenshot nor pasted code: a baseline is then ignored (the model sometimes copies the current code into it).
 */
export function parseCodingReply(text: string, current: CodeFile[], allowBaseline = true): CodingReply {
  const raw = normalizeReply(parseReplyObject(text));
  for (const field of ['problem', 'clarification', 'summary']) {
    if (typeof raw[field] !== 'string' || (raw[field] as string).length > 4000) {
      throw new Error('The AI returned an invalid explanation. Please retry.');
    }
  }
  const clarification = raw['clarification'] as string;
  let language = CODING_LANGUAGES.find(lang => lang === raw['language']);
  if (clarification.trim()) {
    return { language: language || current[0]?.language || 'python', newProblem: false, problem: raw['problem'] as string, clarification,
      summary: raw['summary'] as string, baseline: null, files: current, deletedFileIds: [], notes: [] };
  }
  let baseline: CodeFile[] | null = null;
  if (allowBaseline && raw['baseline'] !== null) {
    validateCodeFiles(raw['baseline'], 'transcribed code');
    baseline = raw['baseline'];
  }
  validateCodeFiles(raw['files'], 'file list');
  const files: CodeFile[] = raw['files'].map(file => ({ ...file }));
  // The files say what the code is written in; the reply's own "language" can be stale (python for a .sql file).
  language = files[0]?.language || language || current[0]?.language || 'python';
  const declared = Array.isArray(raw['deletedFileIds'])
    ? [...new Set(raw['deletedFileIds'].filter((id): id is string => typeof id === 'string'))] : [];
  // A new, separate question does not build on the previous code: nothing to delete or keep.
  let newProblem = raw['newProblem'] === true && baseline === null;
  // Safety net: a reply that replaces every previous file with brand-new ones is a new problem too.
  if (!newProblem && baseline === null && current.length > 0 && files.length > 0
      && current.every(file => declared.includes(file.id)) && files.every(file => !current.some(old => old.id === file.id))) {
    newProblem = true;
  }
  // The model's slips in which files it kept are repaired, never a reason to throw a good answer away.
  let renamed = new Map<string, string>();
  let removed: string[] = [];
  if (!newProblem) {
    // Deletions only count for files that existed and are no longer in the answer.
    removed = declared.filter(id => (baseline ?? current).some(file => file.id === id) && !files.some(file => file.id === id));
    const before = (baseline ?? current).filter(file => !removed.includes(file.id));
    // The same file under a new id keeps its old id, so it shows as a change.
    renamed = alignFileIds(before, files, baseline !== null);
    const missing = before.filter(old => !files.some(file => file.id === old.id));
    if (missing.length && !files.length) {
      throw new Error('The AI omitted a file without an explicit deletion. Nothing was changed; please retry.');
    }
    if (missing.length && missing.length === before.length && baseline === null) {
      // Nothing of the current code was kept: this is a new answer, not a change to the old one.
      newProblem = true;
      removed = [];
    } else {
      // A file the answer left out is kept exactly as it was rather than silently dropped.
      files.push(...missing.filter(old => !files.some(file => file.path === old.path)));
    }
  }
  const before = newProblem ? [] : (baseline ?? current);
  const knownIds = new Set([...before, ...files].map(file => file.id));
  const idByPath = new Map([...before, ...files].map(file => [file.path, file.id] as const));
  const notes: CodingNote[] = [];
  for (const note of Array.isArray(raw['notes']) ? raw['notes'] : []) {
    if (!isRecord(note) || typeof note['text'] !== 'string' || !note['text'].trim()) continue;
    const given = typeof note['fileId'] === 'string' ? renamed.get(note['fileId']) ?? note['fileId'] : '';
    // A note that names its file by filename, or the only file, still belongs to it; any other stray note is dropped.
    const fileId = knownIds.has(given) ? given : idByPath.get(given) ?? (files.length === 1 ? files[0].id : '');
    if (!fileId) continue;
    const anchor = typeof note['anchor'] === 'string' && note['anchor'].length <= 500 ? note['anchor'] : '';
    notes.push({ fileId, anchor, text: note['text'].slice(0, 2000) });
    if (notes.length === 100) break;
  }
  return {
    language,
    newProblem,
    problem: raw['problem'] as string,
    clarification,
    summary: raw['summary'] as string,
    baseline,
    files,
    deletedFileIds: removed,
    notes
  };
}
